import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  approveImageVariants,
  rejectImageVariants,
  type ImageVariant,
  type Platform,
} from '../../lib/api';

interface ImageVariantsGridProps {
  variants: ImageVariant[];
  onVariantsUpdated?: () => void;
  showLinkButton?: boolean;
  onLinkImages?: (assignments: Record<string, string>) => void;
}

export default function ImageVariantsGrid({
  variants,
  onVariantsUpdated,
  showLinkButton = false,
  onLinkImages,
}: ImageVariantsGridProps) {
  const { t } = useTranslation();
  const [selectedVariants, setSelectedVariants] = useState<Set<string>>(new Set());
  const [isApproving, setIsApproving] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);

  // Group variants by platform
  const variantsByPlatform = variants.reduce((acc, variant) => {
    const platform = variant.platform as Platform;
    if (!acc[platform]) {
      acc[platform] = [];
    }
    acc[platform].push(variant);
    return acc;
  }, {} as Record<Platform, ImageVariant[]>);

  const toggleVariant = (variantId: string) => {
    setSelectedVariants((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(variantId)) {
        newSet.delete(variantId);
      } else {
        newSet.add(variantId);
      }
      return newSet;
    });
  };

  const toggleAll = () => {
    if (selectedVariants.size === variants.length) {
      setSelectedVariants(new Set());
    } else {
      setSelectedVariants(new Set(variants.map((v) => v.id)));
    }
  };

  const handleApprove = async () => {
    if (selectedVariants.size === 0) {
      toast.error(t('imageGeneration.errors.noSelection', { defaultValue: 'Please select at least one image' }));
      return;
    }

    setIsApproving(true);
    try {
      await approveImageVariants(Array.from(selectedVariants));
      toast.success(t('imageGeneration.success.approved'));
      setSelectedVariants(new Set());
      onVariantsUpdated?.();
    } catch (error: any) {
      console.error('Failed to approve variants:', error);
      toast.error(error.response?.data?.error || t('imageGeneration.errors.approvalFailed'));
    } finally {
      setIsApproving(false);
    }
  };

  const handleReject = async () => {
    if (selectedVariants.size === 0) {
      toast.error(t('imageGeneration.errors.noSelection', { defaultValue: 'Please select at least one image' }));
      return;
    }

    setIsRejecting(true);
    try {
      await rejectImageVariants(Array.from(selectedVariants));
      toast.success(t('imageGeneration.success.rejected'));
      onVariantsUpdated?.(Array.from(selectedVariants), 'rejected');
      setSelectedVariants(new Set());
    } catch (error: any) {
      console.error('Failed to reject variants:', error);
      toast.error(error.response?.data?.error || t('imageGeneration.errors.rejectionFailed'));
    } finally {
      setIsRejecting(false);
    }
  };

  if (variants.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 dark:text-gray-400">
          {t('imageGeneration.variants.noImagesGenerated')}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Actions Header */}
      <div className="flex items-center justify-between pb-4 border-b-2 border-gray-200 dark:border-gray-800">
        <div className="flex items-center gap-3">
          <button
            onClick={toggleAll}
            className="text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
          >
            {selectedVariants.size === variants.length
              ? t('imageGeneration.variants.deselectAll')
              : t('imageGeneration.variants.selectAll')}
          </button>
          {selectedVariants.size > 0 && (
            <span className="text-sm text-gray-500 dark:text-gray-400">
              ({selectedVariants.size} {t('common.selected', { defaultValue: 'selected' })})
            </span>
          )}
        </div>

        <div className="flex items-center gap-3">
          {selectedVariants.size > 0 && (
            <>
              <button
                onClick={handleReject}
                disabled={isRejecting || isApproving}
                className="px-4 py-2 border-2 border-gray-300 dark:border-gray-700 hover:border-red-500 hover:text-red-500 dark:hover:border-red-400 dark:hover:text-red-400 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isRejecting ? t('common.processing', { defaultValue: 'Processing...' }) : t('imageGeneration.variants.reject')}
              </button>
              <button
                onClick={handleApprove}
                disabled={isApproving || isRejecting}
                className="px-4 py-2 border-2 border-black dark:border-white bg-black dark:bg-white text-white dark:text-black hover:bg-white hover:text-black dark:hover:bg-black dark:hover:text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isApproving ? t('common.processing', { defaultValue: 'Processing...' }) : t('imageGeneration.variants.approve')}
              </button>
            </>
          )}
          {showLinkButton && onLinkImages && (
            <button
              onClick={() => {
                // Build assignments from approved variants (one per platform)
                const assignments: Record<string, string> = {};
                Object.entries(variantsByPlatform).forEach(([platform, platformVariants]) => {
                  const approved = platformVariants.find((v) => v.status === 'approved');
                  if (approved) {
                    assignments[platform] = approved.id;
                  }
                });
                onLinkImages(assignments);
              }}
              className="px-4 py-2 border-2 border-blue-500 dark:border-blue-400 text-blue-500 dark:text-blue-400 hover:bg-blue-500 hover:text-white dark:hover:bg-blue-400 dark:hover:text-black font-medium transition-colors"
            >
              {t('imageGeneration.variants.linkToPost')}
            </button>
          )}
        </div>
      </div>

      {/* Variants Grid - grouped by platform */}
      {Object.entries(variantsByPlatform).map(([platform, platformVariants]) => (
        <div key={platform}>
          {/* Platform Header */}
          <h3 className="text-lg font-bold mb-4 dark:text-white flex items-center gap-2">
            <span className="px-2 py-1 bg-gray-100 dark:bg-gray-900 border-2 border-black dark:border-white text-sm">
              {t(`platforms.${platform}`)}
            </span>
            <span className="text-sm font-normal text-gray-500 dark:text-gray-400">
              ({platformVariants.length} {t('imageGeneration.variantsPerPlatform').toLowerCase()})
            </span>
          </h3>

          {/* Variants Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-6">
            {platformVariants.map((variant) => (
              <div
                key={variant.id}
                className={`relative border-2 rounded overflow-hidden cursor-pointer transition-all ${
                  selectedVariants.has(variant.id)
                    ? 'border-black dark:border-white ring-2 ring-black dark:ring-white'
                    : 'border-gray-200 dark:border-gray-800 hover:border-gray-400 dark:hover:border-gray-600'
                } ${
                  variant.status === 'approved'
                    ? 'ring-2 ring-green-500 dark:ring-green-400'
                    : variant.status === 'rejected'
                    ? 'opacity-50 grayscale'
                    : ''
                }`}
                onClick={() => toggleVariant(variant.id)}
              >
                {/* Image */}
                <div className="aspect-square bg-gray-100 dark:bg-gray-900">
                  {variant.mediaAsset && (
                    <img
                      src={variant.mediaAsset.storageUrl}
                      alt={`${platform} variant`}
                      className="w-full h-full object-cover"
                    />
                  )}
                </div>

                {/* Checkbox Overlay */}
                <div className="absolute top-2 left-2">
                  <input
                    type="checkbox"
                    checked={selectedVariants.has(variant.id)}
                    onChange={() => toggleVariant(variant.id)}
                    className="w-5 h-5 border-2 border-black dark:border-white bg-white dark:bg-gray-900 cursor-pointer"
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>

                {/* Status Badge */}
                {variant.status !== 'generated' && (
                  <div className="absolute top-2 right-2">
                    <span
                      className={`px-2 py-1 text-xs font-bold border-2 ${
                        variant.status === 'approved'
                          ? 'bg-green-500 dark:bg-green-400 border-green-600 dark:border-green-500 text-white dark:text-black'
                          : 'bg-red-500 dark:bg-red-400 border-red-600 dark:border-red-500 text-white dark:text-black'
                      }`}
                    >
                      {variant.status === 'approved'
                        ? t('imageGeneration.variants.approved')
                        : t('imageGeneration.variants.rejected')}
                    </span>
                  </div>
                )}

                {/* Metadata */}
                <div className="p-2 bg-white dark:bg-gray-950 border-t-2 border-gray-200 dark:border-gray-800">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-500 dark:text-gray-400">
                      {variant.width} × {variant.height}
                    </span>
                    {variant.costTokens && (
                      <span className="text-gray-500 dark:text-gray-400 tabular-nums">
                        {variant.costTokens.toLocaleString()} tokens
                      </span>
                    )}
                  </div>
                  {variant.generationTimeMs && (
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {t('imageGeneration.variants.generationTimeMs', { ms: variant.generationTimeMs.toLocaleString() })}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
